/*
* Milestone component specific for Adalia M&E.
*
* Loads project milestones as options (As selection options, each project should contain a list of own milestones only)
*
* Project can be passed as a prop, but if not, this component expects the document to contain an element with an id report-container
* and that element have a data-attribute called project-id.
* 
*/
import React from 'react';
import update from 'immutability-helper';

import {fetchJson} from '../../../helpers/fetch.js';

import {
  Mode,
  DataType,
} from '../common.jsx';
import Helpers from './helpers.jsx';
import Setting from './setting.jsx';

import GridLayout from './grid_layout.jsx';
import DropdownSelectionWithAsyncOptions from './dropdown_selection_with_async_options.jsx';


import { project_milestone_options_for_select_path } from '~/routes';

export default class AdaliaMEMilestone extends GridLayout {

  static get CATEGORY() {
    return 'question';
  }

  static get TYPE() {
    return 'adalia_me_milestone';
  }
  static get TRANSLATED_ATTRIBUTES() {
    return {
      label: 'component_title_adalia_me_milestone',
      description: 'field_default_description',
      help: null,
      blankOptionLabel: 'select_blank_option_default'
    };
  }

  static icon() {
    return <i className='fa fa-flag'></i>;
  }

  static compatiblePrefillComponents() {
    return ['adalia_me_milestone_selector'];
  }

  static compatiblePrefillDataTypes() {
    return [DataType.INTEGER];
  }

  constructor(props) {
    super(props);
    this.addRow = this.addRow.bind(this);
    this.deleteRow = this.deleteRow.bind(this);
    this.addColumn = this.addColumn.bind(this);
    this.deleteColumn = this.deleteColumn.bind(this);
    this.reorderOption = this.reorderOption.bind(this);
    this.state = {}
  }

  static defaultContent(language) {
    let fieldName = 'field_' + Helpers.randomString();

    return Object.assign(super.defaultContent(language), {
      level: 'project',
      name: fieldName,
      rows: [
        [
          {
            span: 1,
            component: Object.assign(AdaliaMEMilestoneSelector.defaultContent(language), {
              name: fieldName + '_dropdown',
              deletable: true,
              sortable: true
            })
          }
        ]
      ]
    });
  }

  componentDidMount() {
    super.componentDidMount();

    if (typeof this.props.updateSettingsCallback == 'function') {
      let milestoneSelector = this.props.content.rows[0][0].component;
      let {reportKey, translations} = this.props.content;
      // this update make the TRANSLATED_ATTRIBUTES need to be copied from milestone selector to this component
      this.props.updateSettingsCallback(
        this.props.content.id,
        update(this.props.content, {$merge: Object.assign(milestoneSelector, {reportKey: reportKey, translations: translations})})
      );
    }
  }

  renderSettings() {
    return <></>
  }
}


export class AdaliaMEMilestoneSelector extends DropdownSelectionWithAsyncOptions {

  static get TYPE() {
    return 'adalia_me_milestone_selector';
  }

  static get TRANSLATED_ATTRIBUTES() {
    return {
      label: 'component_title_adalia_me_milestone',
      description: 'field_default_description',
      help: null,
      blankOptionLabel: 'select_blank_option_default',
      changeConfirmText: null
    };
  }

  static get SETTINGS_DUMMY_DATA() {
    return [
      { value: 1, label: 'Milestone 1'},
      { value: 2, label: 'Milestone 2'},
      { value: 3, label: 'Milestone 3'}
    ];
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      deletable: false,
      sortable: false,
      allowMultiple: false,
      required: false,
      reportKey: '',
      dataType: DataType.INTEGER,
      includeBlank: false,
      blankOptionLabel: 'blankOptionLabel',
      sourcesOfPrefill: '',
    });
  }

  componentDidMount() {
    super.componentDidMount();

    let project_id = this.props.project?.id || jQuery('#report-container').data('project-id');

    if (!project_id || [Mode.EDIT, Mode.SETTINGS].includes(this.props.mode)) {
      this.setState({
        options: this.constructor.SETTINGS_DUMMY_DATA,
        loading: false
      });

    } else {
      // Prefill from the existing answers, if any
      const selectedValue = this.getAnswer();
      if (selectedValue) {
        this.setState({options: [{label: '', value: selectedValue}], loading: false});
      }

      fetchOptionsForSelect(
        project_id,
        this.getAnswer(),
        this.props.handleDisbursement,
        (res) => {
          if (res) {
            this.setState({
              options: res,
              loading: false
            }, () => {
              if (!this.props.content.includeBlank && this.getSelectedOptions() && !this.getRawAnswer()) {
                let answer = this.getAnswer()[0];
                if (this.props.updateAnswerCallback) this.props.updateAnswerCallback(this.fullFieldName(), answer === void 0 ? this.readAnswerFromInputs() : answer);
              }
            })
          }
        });
    }
  }

  onChange = (value) => {
    this.updateAnswer(value);
  };

  componentDidUpdate(prevProps, prevState) {
    super.componentDidUpdate();
  }

  renderSettings() {
    return <div>
      {this.renderDataGridDetails()}
      <Setting.Checkbox name='required' label={I18n.t('field_required', {scope: 'fe'})} value={this.props.content.required} onChange={this.updateSettings} />
      <Setting.TextField name='label' label={I18n.t('field_label', {scope: 'fe'})} value={this.getText('label')} onChange={this.updateSettings} />
      <Setting.Checkbox name='label_hidden' className='label-hidden-check' label={I18n.t('field_label_hide', {scope: 'fe'})} value={this.props.content.label_hidden} onChange={this.updateSettings} />
      <Setting.LongTextField name="description" label={I18n.t('field_description', {scope: 'fe'})} value={this.getText('description')} onChange={this.updateSettings} />
      <Setting.LongTextField name="help" label={I18n.t('field_help', {scope: 'fe'})} value={this.getText('help')} onChange={this.updateSettings} />
      <Setting.Checkbox name="includeBlank" label={I18n.t('field_include_blank', {scope: 'fe'})} value={this.props.content.includeBlank} onChange={this.updateSettings} />
      <Setting.TextField name="blankOptionLabel" label={I18n.t('select_blank_option_label', {scope: 'fe'})} value={this.getText('blankOptionLabel')} onChange={this.updateSettings} />
      <Setting.TextField
        name="reportKey"
        label={I18n.t('field_report_key', {scope: 'fe'})}
        value={this.props.content.reportKey}
        description={I18n.t('field_report_key_description', {scope: 'fe'})}
        {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
      />

      {this.renderSourcesOfPrefillSettings()}

      <Setting.LongTextField
        name='changeConfirmText'
        label={I18n.t('field_change_confirm_text', {scope: 'fe'})}
        value={this.getText('changeConfirmText')}
        onChange={this.updateSettings}
        description={I18n.t('field_change_confirm_text_description', {scope: 'fe'})}
      />
    </div>
  }
}

const fetchOptionsForSelect = (project_id, selected, handlingEnabled, cb) => {
  fetchJson(project_milestone_options_for_select_path({ project_id: project_id })).then(
    (res) => {
      cb(res.map(p => ({ ...p })));
    }
  )
};
